// Prices come from booking-calendar's price rules as a vector: group size -> price PER PERSON,
// incl. VAT. A size that is missing is not sold at that time — never substitute a neighbour.

export function sizesOf(vector) {
    return Object.keys(vector || {})
        .map(Number)
        .filter(n => Number.isInteger(n) && n > 0)
        .sort((a, b) => a - b);
}

// totalFor is what a group of `players` pays in all, or null when that size is not sold.
export function totalFor(vector, players) {
    const perPerson = vector?.[players];
    return perPerson == null ? null : perPerson * players;
}

// extraPlayersPrice is the website's rule for a group that turns up with more players than it
// booked: the extra players pay the per-person price of the group size they end up as, and the
// seats already paid keep their price.
//
// What was paid for is not always the head count. A booking below the smallest size sold at that
// time paid for that size — three players at a "2-4" slot bought four seats — so a fourth guest
// owes nothing (covered). `paid` is what the booking already covers, and the extras are counted
// from there: (n − paid) × p(n).
//
// Above the largest size the room is sold to, the rules say nothing — but we do let a group go over
// the recommended number on special occasions. Those players are then charged at the largest size's
// per-person price (the last step keeps applying), and overMax tells the page to say so.
export function extraPlayersPrice(vector, booked, arriving) {
    const m = Number(booked);
    const n = Number(arriving);
    if (!Number.isInteger(m) || !Number.isInteger(n) || m < 1 || n <= m) return { error: "invalid" };
    const sizes = sizesOf(vector);
    if (!sizes.length) return { error: "no_price" };
    const min = sizes[0];
    const max = sizes[sizes.length - 1];
    const paid = Math.max(m, min);
    if (n <= paid) return { paid, extra: 0, perPerson: 0, amount: 0, covered: true, overMax: false, max };
    const overMax = n > max;
    const perPerson = overMax ? vector[max] : vector[n];
    if (perPerson == null) return { error: "not_sold" };
    return { paid, extra: n - paid, perPerson, amount: (n - paid) * perPerson, covered: false, overMax, max };
}

// ROOM_MIN_PLAYERS is the smallest group any room takes. It is a fact about the business, not
// something the price rules state: a time band sold only to 4 and up still takes a couple, they
// just pay for 4. Hard-coded on purpose — if a room ever sells to one player, this is the line to
// change (and then it belongs in the price rules).
export const ROOM_MIN_PLAYERS = 2;

// priceTiers is the price steps a slot is sold in, each with the smallest group it covers.
//
// The cheapest step reaches down to ROOM_MIN_PLAYERS, so an afternoon sold to 4 and up reads
// "2-4" rather than "4". The price is the same either way; the label is what keeps a GM from
// thinking a couple has to be turned away.
export function priceTiers(vector, roomMin = ROOM_MIN_PLAYERS) {
    const sizes = sizesOf(vector);
    return sizes.map((size, i) => ({
        size,
        from: i === 0 && Number.isInteger(roomMin) && roomMin < size ? roomMin : size,
        perPerson: vector[size],
        total: vector[size] * size,
    }));
}

// walkInPrice is what a walk-in of `players` costs in all.
//
// Between the smallest and largest size sold it is that size's own price. A group below the
// smallest size pays for the smallest size (belowMin) — the step covers them. A group above the
// largest pays the largest size's per-person price per head (overMax, the same allowance as
// extraPlayersPrice). Both are flagged so the page can say which one is happening.
export function walkInPrice(vector, players) {
    const n = Number(players);
    if (!Number.isInteger(n) || n < 1) return { error: "invalid" };
    const sizes = sizesOf(vector);
    if (!sizes.length) return { error: "no_price" };
    const min = sizes[0];
    const max = sizes[sizes.length - 1];
    const belowMin = n < min;
    const overMax = n > max;
    const tier = belowMin ? min : overMax ? max : n;
    const perPerson = vector[tier];
    if (perPerson == null) return { error: "not_sold" };
    // Below the smallest size the group buys that size's seats; otherwise every head pays.
    const heads = belowMin ? min : n;
    return { perPerson, amount: perPerson * heads, tier, belowMin, overMax, min, max };
}

// bookedPlayers reads the booked group size off a booking's player label ("4"). A range label
// ("2-4") says nothing about what was paid for, so it gives null and the GM types the number.
export function bookedPlayers(label) {
    const s = String(label ?? "").trim();
    return /^\d+$/.test(s) ? Number(s) : null;
}
